import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import { useEnrichedAuth } from './hooks/useEnrichedAuth';
import { usePreferenceLoader } from './hooks/usePreferenceLoader';
import { ProtectedRoute } from './components/ccms-components/auth/components/ProtectedRoute';
import { LoadingScreen } from './components/common/LoadingScreen';
import VendorGuard from './components/guards/VendorGuard';

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { OnboardingLayout } from './layouts/OnboardingLayout';
import MainLayout from './layouts/MainLayout';

// Auth Pages (eager loaded)
import LoginPage from './pages/auth/login';
import ForgotPasswordPage from './pages/auth/forgot-password';
import ResetPasswordPage from './pages/auth/reset-password';
import VendorRegisterPage from './pages/auth/vendor-register';
import OnboardingPage from './pages/auth/onboarding';

// Main Pages (lazy loaded)
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const SettingsPage = lazy(() => import('./pages/settings'));
const UserProfilePage = lazy(() => import('./pages/profile'));

// Business Pages (lazy loaded)
const ProfilePage = lazy(() => import('./pages/app/MyBusiness/profile'));

// Products Pages (lazy loaded)
const ProductsListPage = lazy(() => import('./pages/app/Products/list'));
const ProductCreatePage = lazy(() => import('./pages/app/Products/Create'));
const ProductViewPage = lazy(() => import('./pages/app/Products/View'));
const ProductBulkImportPage = lazy(() => import('./pages/app/Products/BulkImport'));

// Ratings Pages (lazy loaded)
const RatingsListPage = lazy(() => import('./pages/app/Ratings/list'));

// Events Pages (lazy loaded)
const EventsListPage = lazy(() => import('./pages/app/Events/list'));
const EventViewPage = lazy(() => import('./pages/app/Events/View'));

// Orders Pages (lazy loaded)
const OrdersListPage = lazy(() => import('./pages/app/Orders/list'));
const OrderViewPage = lazy(() => import('./pages/app/Orders/View'));

// Finance Pages (lazy loaded)
const EarningsPage = lazy(() => import('./pages/app/Finance/Earnings'));
const StatementPage = lazy(() => import('./pages/app/Finance/Statement'));
const FinancePayoutsPage = lazy(() => import('./pages/app/Finance/Payouts'));

// Logs Pages (lazy loaded)
const LogsListPage = lazy(() => import('./pages/app/Logs/list'));

// Legal Pages (lazy loaded - public access)
const TermsPage = lazy(() => import('./pages/legal/terms'));
const PrivacyPage = lazy(() => import('./pages/legal/privacy'));

/**
 * Page loader wrapper for lazy-loaded components
 */
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      {children}
    </Suspense>
  );
}

/**
 * Vendor page wrapper - requires auth, onboarding, and vendor
 */
function VendorPage({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useEnrichedAuth();
  const { hasLocalPreferences } = usePreferenceLoader();

  // Check if onboarding is required
  const requiresOnboarding = isAuthenticated && user && (
    !user.onboardingCompleted ||
    (user.onboardingCompleted && !hasLocalPreferences)
  );

  if (requiresOnboarding) {
    return <Navigate to="/auth/onboarding" replace />;
  }

  return (
    <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
      <VendorGuard redirectTo="/auth/vendor-register">
        <MainLayout>
          <PageLoader>{children}</PageLoader>
        </MainLayout>
      </VendorGuard>
    </ProtectedRoute>
  );
}

function App() {
  const { isAuthenticated, user, isLoading } = useEnrichedAuth();
  const { hasLocalPreferences, isLoadingPreferences } = usePreferenceLoader();

  // Show loading screen while auth is loading OR preferences are loading
  if (isLoading || (isAuthenticated && isLoadingPreferences)) {
    return <LoadingScreen />;
  }

  // Force onboarding if:
  // 1. User has NOT completed onboarding, OR
  // 2. User HAS completed onboarding BUT has no local preferences
  const requiresOnboarding = isAuthenticated && user && (
    !user.onboardingCompleted ||
    (user.onboardingCompleted && !hasLocalPreferences)
  );

  return (
    <Routes>
      {/* ==================== PUBLIC ROUTES ==================== */}
      {/* Terms and Conditions - Public access */}
      <Route
        path="/terms"
        element={
          <PageLoader>
            <TermsPage />
          </PageLoader>
        }
      />

      {/* Privacy Policy - Public access */}
      <Route
        path="/privacy"
        element={
          <PageLoader>
            <PrivacyPage />
          </PageLoader>
        }
      />

      {/* ==================== AUTH ROUTES ==================== */}

      {/* Login */}
      <Route
        path="/auth/login"
        element={
          isAuthenticated ? (
            requiresOnboarding ? (
              <Navigate to="/auth/onboarding" replace />
            ) : (
              <Navigate to="/" replace />
            )
          ) : (
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          )
        }
      />

      {/* Forgot Password */}
      <Route
        path="/auth/forgot-password"
        element={
          isAuthenticated ? (
            requiresOnboarding ? (
              <Navigate to="/auth/onboarding" replace />
            ) : (
              <Navigate to="/" replace />
            )
          ) : (
            <AuthLayout showBackButton backTo="/auth/login">
              <ForgotPasswordPage />
            </AuthLayout>
          )
        }
      />

      {/* Reset Password */}
      <Route
        path="/auth/reset-password"
        element={
          isAuthenticated ? (
            requiresOnboarding ? (
              <Navigate to="/auth/onboarding" replace />
            ) : (
              <Navigate to="/" replace />
            )
          ) : (
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          )
        }
      />

      {/* Vendor Registration - for authenticated users without a vendor */}
      <Route
        path="/auth/vendor-register"
        element={
          isAuthenticated ? (
            requiresOnboarding ? (
              <Navigate to="/auth/onboarding" replace />
            ) : (
              <AuthLayout showBackButton backTo="/" cardMaxWidth="sm">
                <VendorRegisterPage />
              </AuthLayout>
            )
          ) : (
            <AuthLayout showBackButton backTo="/auth/login" cardMaxWidth="sm">
              <VendorRegisterPage />
            </AuthLayout>
          )
        }
      />

      {/* Onboarding */}
      <Route
        path="/auth/onboarding"
        element={
          <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
            <OnboardingLayout>
              <OnboardingPage />
            </OnboardingLayout>
          </ProtectedRoute>
        }
      />

      {/* ==================== VENDOR ROUTES ==================== */}

      {/* Dashboard - Main landing page */}
      <Route
        path="/"
        element={
          <VendorPage>
            <DashboardPage />
          </VendorPage>
        }
      />

      {/* Business Profile */}
      <Route
        path="/profile"
        element={
          <VendorPage>
            <ProfilePage />
          </VendorPage>
        }
      />

      {/* Locations - Redirect to profile with locations tab */}
      <Route path="/locations" element={<Navigate to="/profile?tab=locations" replace />} />

      {/* Documents - Redirect to profile with documents tab */}
      <Route path="/documents" element={<Navigate to="/profile?tab=documents" replace />} />

      {/* Products */}
      <Route
        path="/products"
        element={
          <VendorPage>
            <ProductsListPage />
          </VendorPage>
        }
      />
      <Route
        path="/products/create"
        element={
          <VendorPage>
            <ProductCreatePage />
          </VendorPage>
        }
      />
      <Route
        path="/products/bulk-import"
        element={
          <VendorPage>
            <ProductBulkImportPage />
          </VendorPage>
        }
      />
      <Route
        path="/products/:id"
        element={
          <VendorPage>
            <ProductViewPage />
          </VendorPage>
        }
      />

      {/* Ratings */}
      <Route
        path="/ratings"
        element={
          <VendorPage>
            <RatingsListPage />
          </VendorPage>
        }
      />

      {/* Events */}
      <Route
        path="/events"
        element={
          <VendorPage>
            <EventsListPage />
          </VendorPage>
        }
      />
      <Route
        path="/events/:id"
        element={
          <VendorPage>
            <EventViewPage />
          </VendorPage>
        }
      />

      {/* Orders */}
      <Route
        path="/orders"
        element={
          <VendorPage>
            <OrdersListPage />
          </VendorPage>
        }
      />
      <Route
        path="/orders/view/:id"
        element={
          <VendorPage>
            <OrderViewPage />
          </VendorPage>
        }
      />

      {/* Finance */}
      <Route
        path="/finance/earnings"
        element={
          <VendorPage>
            <EarningsPage />
          </VendorPage>
        }
      />
      <Route
        path="/finance/statement"
        element={
          <VendorPage>
            <StatementPage />
          </VendorPage>
        }
      />
      <Route
        path="/finance/payouts"
        element={
          <VendorPage>
            <FinancePayoutsPage />
          </VendorPage>
        }
      />

      {/* Logs */}
      <Route
        path="/logs"
        element={
          <VendorPage>
            <LogsListPage />
          </VendorPage>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <VendorPage>
            <SettingsPage />
          </VendorPage>
        }
      />

      {/* User Profile (Account) */}
      <Route
        path="/account"
        element={
          <VendorPage>
            <UserProfilePage />
          </VendorPage>
        }
      />

      {/* ==================== LEGACY REDIRECTS ==================== */}
      {/* Redirect old domain routes to new flat routes */}
      <Route path="/app/my-business" element={<Navigate to="/" replace />} />
      <Route path="/app/my-business/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/app/my-business/profile" element={<Navigate to="/profile" replace />} />
      <Route path="/app/my-business/locations" element={<Navigate to="/profile?tab=locations" replace />} />
      <Route path="/app/my-business/documents" element={<Navigate to="/profile?tab=documents" replace />} />
      <Route path="/app/products" element={<Navigate to="/products" replace />} />
      <Route path="/app/products/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/app/products/list" element={<Navigate to="/products" replace />} />
      <Route path="/app/products/create" element={<Navigate to="/products/create" replace />} />
      <Route path="/app/products/:id" element={<Navigate to="/products/:id" replace />} />
      <Route path="/app/ratings" element={<Navigate to="/ratings" replace />} />
      <Route path="/app/ratings/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/app/ratings/list" element={<Navigate to="/ratings" replace />} />
      <Route path="/app/events" element={<Navigate to="/events" replace />} />
      <Route path="/app/events/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/app/events/list" element={<Navigate to="/events" replace />} />
      <Route path="/app/events/:id" element={<Navigate to="/events/:id" replace />} />
      <Route path="/app/orders" element={<Navigate to="/orders" replace />} />
      <Route path="/app/orders/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/app/orders/list" element={<Navigate to="/orders" replace />} />
      <Route path="/app/orders/view/:id" element={<Navigate to="/orders/view/:id" replace />} />

      {/* ==================== CATCH ALL ==================== */}
      <Route
        path="*"
        element={
          <Navigate
            to={
              isAuthenticated
                ? requiresOnboarding
                  ? '/auth/onboarding'
                  : '/'
                : '/auth/login'
            }
            replace
          />
        }
      />
    </Routes>
  );
}

export default App;
